import { useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { toast, ToastContainer } from 'react-toastify';
import { NATIVE_MINT } from '@solana/spl-token';
import { useLocalStorageState, $Number, $String, $StringArray, $NumberArray, DataType } from '@sasha.p/use-persistent-state';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getCompilerVersions } from '@agnostico/browser-solidity-compiler';
import { Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { Connection } from '@solana/web3.js';
import { providers } from 'ethers';

import { Navbar } from './Components/Navbar';
import { Feed } from './pages/Feed/Feed';
import { Route, Routes, useLocation } from 'react-router-dom';
import { TokenCreate } from './pages/TokenCreate/TokenCreate';
import { Header } from './Components/Header';
import { useBackend } from './Hooks/UseBackend';
import { Result } from './pages/ResultPage';
import { SettingsPage, UserSettings } from './pages/Settings';
import { ErcTokenCreate } from './pages/ErcTokenCreate';
import { solididtyReleases } from './utils/types';
import Modal from './Components/Modal';
import { useSourceMaintainer } from './Hooks/sources/useSourceMaintainer';
import { SolPriceProvider } from './Components/SolPriceProvider';
import { getEthereumWallet } from './utils/ethereum/wallet';
import { getSolanaWallet } from './utils/solana/wallet';
import { TweetTypesProvider } from './pages/Feed/context/TweetTypesContext';
import { SoundStoreProvider } from './pages/Feed/context/SoundStoreContext';
import { AppMode } from './types';

import 'react-toastify/dist/ReactToastify.css';
import { Api } from '@sodamnfoolish/sjc-backend-types';

function AuthorizedApp({ user }: { user: Api.Auth.User }) {
    const [settings, setSettings] = useLocalStorageState<{ mode: AppMode } & UserSettings>(
        {
            mode: $String({ defaultValue: 'deploy' }) as DataType<AppMode, AppMode>,
            solInitialBuyArrow: $Number({ defaultValue: 3 }),
            solInitialBuyCopy: $Number({ defaultValue: 3 }),
            ethLiquidityArrow: $Number({ defaultValue: 3 }),
            ethLiquidityCopy: $Number({ defaultValue: 3 }),
            solSnipeArrow: $Number({ defaultValue: 3 }),
            solSnipeCopy: $Number({ defaultValue: 3 }),
            ethSnipeValues: $NumberArray({ defaultValue: [] }),
            solBuyTxCost: $Number({ defaultValue: 0.001 }),
            solSellTxCost: $Number({ defaultValue: 0.001 }),
            autoSellDelayArrow: $Number({ defaultValue: 10 }),
            autoSellDelayCreation: $Number({ defaultValue: 10 }),
            autoSellProfitArrow: $Number({ defaultValue: 50 }),
            autoSellProfitCreation: $Number({ defaultValue: 50 }),
            ethPrivateKey: $String({ defaultValue: '' }),
            solanaPrivateKey: $String({ defaultValue: '' }),
            solanaSniperPrivateKey: $String({ defaultValue: '' }),
            ethSniperPrivateKeys: $StringArray({ defaultValue: [] }),
            ethDisperserPrivateKey: $String({ defaultValue: '' }),
            zeroSlotBuyTip: $Number({ defaultValue: 0.00011 }),
            zeroSlotSellTip: $Number({ defaultValue: 0.00011 }),
            solRpcUrl: $String({ defaultValue: 'https://ofelia-3bh1h8-fast-mainnet.helius-rpc.com' }),
            ethRpcUrl: $String({ defaultValue: 'https://small-holy-dew.quiknode.pro/fbe4e898c2491573e0ef6d4928ec8c150377eefe' }),

            purchasedSolTokens: $StringArray({ defaultValue: [] }),
            purchasedSolTokensNames: $StringArray({ defaultValue: [] }),
            purchasedSolTokensSymbols: $StringArray({ defaultValue: [] }),
            purchasedSolTokensImages: $StringArray({ defaultValue: [] }),

            deployedSolTokens: $StringArray({ defaultValue: [] }),
            deployedSolTokensNames: $StringArray({ defaultValue: [] }),
            deployedSolTokensSymbols: $StringArray({ defaultValue: [] }),
            deployedSolTokensImages: $StringArray({ defaultValue: [] }),
            telegramAPIID: $String(),
            telegramAPIHash: $String(),
            telegramPhoneNumber: $String(),
        },
        { key: 'settings' },
    );

    const [sources, setSources] = useState<Source[]>([]);
    const [visibleSources, setVisibleSources] = useState<Source[]>([]);
    const [isGeneralPostsVisible, setGeneralPostsVisible] = useState(true);
    const [isPumpFunsPostsVisible, setIsPumpFunsPostsVisible] = useState<boolean>(true);
    const [isBonkPostsVisible, setIsBonkPostsVisible] = useState<boolean>(true);
    const [sourceOrder, setSourceOrder] = useState<string[]>(() => {
        const savedOrder = localStorage.getItem('sourceOrder');
        return savedOrder ? JSON.parse(savedOrder) : [];
    });

    const [solidityReleases, setSolidityReleases] = useState<solididtyReleases>({});
    useEffect(() => {
        getCompilerVersions().then((response: any) => setSolidityReleases(response.releases));
    }, []);

    const location = useLocation();
    const locationWithoutHeader = ['/Settings', '/ResultPage', '/TokenCreate', '/ErcTokenCreate'].includes(location.pathname);

    const { getSources } = useBackend();

    useEffect(() => {
        getSources().then((data) => {
            setSources(data);

            let savedOrder = sourceOrder;
            if (sourceOrder.length === 0) {
                const storedOrder = localStorage.getItem('sourceOrder');
                savedOrder = storedOrder ? JSON.parse(storedOrder) : data.map((source) => source.username);
            }

            const updatedOrder = [...savedOrder];
            data.forEach((source) => {
                if (!updatedOrder.includes(source.username)) {
                    updatedOrder.push(source.username);
                }
            });

            setSourceOrder(updatedOrder);
            localStorage.setItem('sourceOrder', JSON.stringify(updatedOrder));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReorder = (draggedUsername: string, hoveredUsername: string) => {
        const draggedIndex = sourceOrder.indexOf(draggedUsername);
        const hoveredIndex = sourceOrder.indexOf(hoveredUsername);

        if (draggedIndex !== -1 && hoveredIndex !== -1) {
            const newOrder = [...sourceOrder];
            newOrder.splice(draggedIndex, 1);
            newOrder.splice(hoveredIndex, 0, draggedUsername);
            setSourceOrder(newOrder);

            localStorage.setItem('sourceOrder', JSON.stringify(newOrder));
        }
    };

    const handleToggleSource = (source: Source, checked: boolean) => {
        setVisibleSources((prev) => {
            const updatedSources = checked ? [...prev, source] : prev.filter((s) => s.username !== source.username);

            localStorage.setItem('visibleSources', JSON.stringify(updatedSources.map((s) => s.username)));
            return updatedSources;
        });
    };

    useEffect(() => {
        const storedUsernames = localStorage.getItem('visibleSources');
        if (storedUsernames) {
            const usernames = JSON.parse(storedUsernames) as string[];
            const restoredSources = sources.filter((s) => usernames.includes(s.username));
            setVisibleSources(restoredSources);
        } else {
            setVisibleSources(sources);
        }
    }, [sources]);

    const visibleGeneralPosts = (checked: boolean) => {
        setGeneralPostsVisible(checked);
        localStorage.setItem('isGeneralPostsVisible', JSON.stringify(checked));
    };

    useEffect(() => {
        const storedValue = localStorage.getItem('isGeneralPostsVisible');
        if (storedValue !== null) {
            setGeneralPostsVisible(JSON.parse(storedValue));
        }
    }, []);

    const visiblePumpFunsPosts = (checked: boolean) => {
        setIsPumpFunsPostsVisible(checked);
        localStorage.setItem('isPumpFunsPostsVisible', JSON.stringify(checked));
    };

    const visibleBonkPosts = (checked: boolean) => {
        setIsBonkPostsVisible(checked);
        localStorage.setItem('isBonkPostsVisible', JSON.stringify(checked));
    };

    useEffect(() => {
        const storedValue = localStorage.getItem('isPumpFunsPostsVisible');
        if (storedValue !== null) {
            setIsPumpFunsPostsVisible(JSON.parse(storedValue));
        }
    }, []);

    const [feedDisplayOpen, setFeedDisplayOpen] = useState(false);

    useSourceMaintainer({
        onRegisterSource: (source) => {
            try {
                const newSources = [...sources, source];
                setSources(newSources);
                console.log('Source maintenance started', source);
                toast.success(`Source maintenance started ${source.username}`);
            } catch (err) {
                console.error(err);
                toast.error(`Failed to handle Source Maintanance Started event`);
            }
        },
        onUnregisterSource: (source) => {
            try {
                console.log('Source maintenance stopped', source);
                const newSources = sources.filter((s) => s !== source);
                setSources(newSources);
                toast.error(`Source maintenance stopped ${source.username}`);
            } catch (err) {
                console.error(err);
                toast.error(`Failed to handle Source Maintanance Stopped event`);
            }
        },
    });

    const solRpcUrl = settings.solRpcUrl;
    const solConnection = useMemo(() => {
        return new Connection(solRpcUrl, 'processed');
    }, [solRpcUrl]);

    const ethRpcUrl = settings.ethRpcUrl;
    const ethProvider = useMemo(() => {
        return new providers.JsonRpcProvider(ethRpcUrl);
    }, [ethRpcUrl]);

    return (
        <DndProvider backend={HTML5Backend}>
            <div className='bg-night-black font-geist min-h-screen min-w-screen inline-block'>
                <SolPriceProvider>
                    <Navbar
                        user={user}
                        mode={settings.mode}
                        getEthWalletStatus={() => getEthereumWallet(settings.ethPrivateKey, ethProvider)}
                        getSolWalletStatus={() => getSolanaWallet(settings.solanaPrivateKey, solConnection)}
                        onFeedDisplayClick={() => {
                            setFeedDisplayOpen(!feedDisplayOpen);
                        }}
                        onModeChange={(mode) => {
                            setSettings((prev) => ({
                                ...prev,
                                mode,
                            }));
                        }}
                    />
                </SolPriceProvider>

                <Routes>
                    <Route
                        path='/'
                        element={
                            <TweetTypesProvider sources={sources}>
                                <SoundStoreProvider sources={sources}>
                                    <Feed
                                        sources={visibleSources}
                                        isGeneralPostsVisible={isGeneralPostsVisible}
                                        isPumpFunsPostsVisible={isPumpFunsPostsVisible}
                                        isBonkPostsVisible={isBonkPostsVisible}
                                        sourceOrder={sourceOrder}
                                        solidityReleases={solidityReleases}
                                        connection={solConnection}
                                        ethProvider={ethProvider}
                                        settings={settings}
                                        onAddPurchasedSolToken={(address: string, name: string, symbol: string, image: string) => {
                                            setSettings((prev) => {
                                                if (prev.purchasedSolTokens.includes(address)) {
                                                    return prev;
                                                }

                                                return {
                                                    ...prev,
                                                    purchasedSolTokens: [...prev.purchasedSolTokens, address],
                                                    purchasedSolTokensNames: [...prev.purchasedSolTokensNames, name],
                                                    purchasedSolTokensSymbols: [...prev.purchasedSolTokensSymbols, symbol],
                                                    purchasedSolTokensImages: [...prev.purchasedSolTokensImages, image],
                                                };
                                            });
                                        }}
                                        onAddDeployedSolToken={(address: string, name: string, symbol: string, image: string) => {
                                            setSettings((prev) => {
                                                if (prev.deployedSolTokens.includes(address)) {
                                                    return prev;
                                                }

                                                return {
                                                    ...prev,
                                                    deployedSolTokens: [...prev.deployedSolTokens, address],
                                                    deployedSolTokensNames: [...prev.deployedSolTokensNames, name],
                                                    deployedSolTokensSymbols: [...prev.deployedSolTokensSymbols, symbol],
                                                    deployedSolTokensImages: [...prev.deployedSolTokensImages, image],
                                                };
                                            });
                                        }}
                                    />
                                </SoundStoreProvider>
                            </TweetTypesProvider>
                        }
                    />
                    <Route
                        path='TokenCreate'
                        element={
                            <TokenCreate
                                defaultEthLiquidity={settings.ethLiquidityCopy}
                                defaultDevBuySolana={settings.solInitialBuyCopy}
                                defaultSnipeBuySolana={settings.solSnipeCopy}
                                autoSellDelay={settings.autoSellDelayCreation}
                                autoSellProfit={settings.autoSellProfitCreation}
                                ethPrivateKey={settings.ethPrivateKey}
                                solPrivateKey={settings.solanaPrivateKey}
                                solSniperPrivateKey={settings.solanaSniperPrivateKey}
                                ethSniperPrivateKeys={settings.ethSniperPrivateKeys}
                                ethSnipeValues={settings.ethSnipeValues}
                                solidityReleases={solidityReleases}
                                zeroSlotBuyTip={settings.zeroSlotBuyTip}
                                buyTxCost={settings.solBuyTxCost}
                                solConnection={solConnection}
                                ethProvider={ethProvider}
                                onAddDeployedSolToken={(address: string, name: string, symbol: string, image: string) => {
                                    setSettings((prev) => {
                                        if (prev.deployedSolTokens.includes(address)) {
                                            return prev;
                                        }

                                        return {
                                            ...prev,
                                            deployedSolTokens: [...prev.deployedSolTokens, address],
                                            deployedSolTokensNames: [...prev.deployedSolTokensNames, name],
                                            deployedSolTokensSymbols: [...prev.deployedSolTokensSymbols, symbol],
                                            deployedSolTokensImages: [...prev.deployedSolTokensImages, image],
                                        };
                                    });
                                }}
                                telegramAPIID={settings.telegramAPIID}
                                telegramAPIHash={settings.telegramAPIHash}
                                telegramPhoneNumber={settings.telegramPhoneNumber}
                            />
                        }
                    />

                    <Route
                        path='Result'
                        element={
                            <Result
                                solPrivateKey={settings.solanaPrivateKey}
                                zeroSlotSellTip={settings.zeroSlotSellTip}
                                solConnection={solConnection}
                                solSniperPrivateKey={settings.solanaSniperPrivateKey}
                                deployedTokens={settings.deployedSolTokens.map((address, index) => ({
                                    address,
                                    name: settings.deployedSolTokensNames[index],
                                    symbol: settings.deployedSolTokensSymbols[index],
                                    imageSrc: settings.deployedSolTokensImages[index],
                                }))}
                                purchasedTokens={settings.purchasedSolTokens.map((address, index) => ({
                                    address,
                                    name: settings.purchasedSolTokensNames[index],
                                    symbol: settings.purchasedSolTokensSymbols[index],
                                    imageSrc: settings.purchasedSolTokensImages[index],
                                }))}
                                sellTxCost={settings.solSellTxCost}
                            />
                        }
                    />
                    <Route
                        path='Settings'
                        element={
                            <SettingsPage
                                solInitialBuyArrow={settings.solInitialBuyArrow}
                                onSolInitialBuyArrowChange={(solInitialBuyArrow) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        solInitialBuyArrow,
                                    }));
                                }}
                                solInitialBuyCopy={settings.solInitialBuyCopy}
                                onSolInitialBuyCopyChange={(solInitialBuyCopy) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        solInitialBuyCopy,
                                    }));
                                }}
                                ethLiquidityArrow={settings.ethLiquidityArrow}
                                onEthLiquidityArrowChange={(ethLiquidityArrow) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        ethLiquidityArrow,
                                    }));
                                }}
                                ethLiquidityCopy={settings.ethLiquidityCopy}
                                onEthLiquidityCopyChange={(ethLiquidityCopy) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        ethLiquidityCopy,
                                    }));
                                }}
                                solSnipeArrow={settings.solSnipeArrow}
                                onSolSnipeArrowChange={(solSnipeArrow) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        solSnipeArrow,
                                    }));
                                }}
                                solSnipeCopy={settings.solSnipeCopy}
                                onSolSnipeCopyChange={(solSnipeCopy) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        solSnipeCopy,
                                    }));
                                }}
                                ethSnipeValues={settings.ethSnipeValues}
                                onEthSnipeValuesChange={(ethSnipeValues) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        ethSnipeValues,
                                    }));
                                }}
                                solBuyTxCost={settings.solBuyTxCost}
                                onSolBuyTxCostChange={(solBuyTxCost) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        solBuyTxCost,
                                    }));
                                }}
                                solSellTxCost={settings.solSellTxCost}
                                onSolSellTxCostChange={(solSellTxCost) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        solSellTxCost,
                                    }));
                                }}
                                autoSellDelayArrow={settings.autoSellDelayArrow}
                                onAutoSellDelayArrowChange={(autoSellDelayArrow) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        autoSellDelayArrow,
                                    }));
                                }}
                                autoSellProfitArrow={settings.autoSellProfitArrow}
                                onAutoSellProfitArrowChange={(autoSellProfitArrow) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        autoSellProfitArrow,
                                    }));
                                }}
                                autoSellDelayCreation={settings.autoSellDelayCreation}
                                onAutoSellDelayCreationChange={(autoSellDelayCreation) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        autoSellDelayCreation,
                                    }));
                                }}
                                autoSellProfitCreation={settings.autoSellProfitCreation}
                                onAutoSellProfitCreationChange={(autoSellProfitCreation) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        autoSellProfitCreation,
                                    }));
                                }}
                                ethPrivateKey={settings.ethPrivateKey}
                                onEthPrivateKeyChange={(ethPrivateKey) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        ethPrivateKey,
                                    }));
                                }}
                                solanaPrivateKey={settings.solanaPrivateKey}
                                onSolanaPrivateKeyChange={(solanaPrivateKey) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        solanaPrivateKey,
                                    }));
                                }}
                                solanaSniperPrivateKey={settings.solanaSniperPrivateKey}
                                onSolanaSniperPrivateKeyChange={(solanaSniperPrivateKey) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        solanaSniperPrivateKey,
                                    }));
                                }}
                                ethSniperPrivateKeys={settings.ethSniperPrivateKeys}
                                onEthSniperPrivateKeysChange={(ethSniperPrivateKeys) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        ethSniperPrivateKeys,
                                    }));
                                }}
                                ethDisperserPrivateKey={settings.ethDisperserPrivateKey}
                                onEthDisperserPrivateKeyChange={(ethDisperserPrivateKey) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        ethDisperserPrivateKey,
                                    }));
                                }}
                                zeroSlotSellTip={settings.zeroSlotSellTip}
                                onZeroSlotSellTipChange={(zeroSlotSellTip) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        zeroSlotSellTip,
                                    }));
                                }}
                                zeroSlotBuyTip={settings.zeroSlotBuyTip}
                                onZeroSlotBuyTipChange={(zeroSlotBuyTip) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        zeroSlotBuyTip,
                                    }));
                                }}
                                solRpcUrl={solRpcUrl}
                                onSolRpcUrlChange={(url) => {
                                    let connection: Connection;
                                    try {
                                        connection = new Connection(url, 'processed');
                                    } catch (e) {
                                        toast.error(`Invalid Solana RPC URL`);
                                        return;
                                    }

                                    connection
                                        .getBalance(NATIVE_MINT)
                                        .then(() => {
                                            setSettings((prev) => ({
                                                ...prev,
                                                solRpcUrl: url,
                                            }));
                                            toast.success(`SOL RPC URL is set to ${url}`);
                                        })
                                        .catch((e) => {
                                            toast.error(`Failed to fetch SOL RPC`);
                                        });
                                }}
                                solConnection={solConnection}
                                ethRpcUrl={ethRpcUrl}
                                onEthRpcUrlChange={(url) => {
                                    let provider: providers.JsonRpcProvider;
                                    try {
                                        provider = new providers.JsonRpcProvider(url);
                                    } catch (e) {
                                        toast.error(`Invalid Ethereum RPC URL`);
                                        return;
                                    }
                                    console.log('url', url);
                                    provider
                                        .getBlockNumber()
                                        .then(() => {
                                            setSettings((prev) => ({
                                                ...prev,
                                                ethRpcUrl: url,
                                            }));
                                            toast.success(`ETH RPC URL is set to ${url}`);
                                        })
                                        .catch(() => {
                                            toast.error(`Failed to fetch ETH RPC`);
                                        });
                                }}
                                ethProvider={ethProvider}
                                deployedSolTokens={settings.deployedSolTokens}
                                deployedSolTokensNames={settings.deployedSolTokensNames}
                                deployedSolTokensSymbols={settings.deployedSolTokensSymbols}
                                deployedSolTokensImages={settings.deployedSolTokensImages}
                                onSetDeployedSolTokens={(tokens, names, symbols, images) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        deployedSolTokens: tokens,
                                        deployedSolTokensNames: names,
                                        deployedSolTokensSymbols: symbols,
                                        deployedSolTokensImages: images,
                                    }));
                                }}
                                purchasedSolTokens={settings.purchasedSolTokens}
                                purchasedSolTokensNames={settings.purchasedSolTokensNames}
                                purchasedSolTokensSymbols={settings.purchasedSolTokensSymbols}
                                purchasedSolTokensImages={settings.purchasedSolTokensImages}
                                onSetPurchasedSolTokens={(tokens, names, symbols, images) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        purchasedSolTokens: tokens,
                                        purchasedSolTokensNames: names,
                                        purchasedSolTokensSymbols: symbols,
                                        purchasedSolTokensImages: images,
                                    }));
                                }}
                                telegramAPIID={settings.telegramAPIID}
                                onTelegramAPIIDChange={(telegramAPIID) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        telegramAPIID,
                                    }));
                                }}
                                telegramAPIHash={settings.telegramAPIHash}
                                onTelegramAPIHashChange={(telegramAPIHash) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        telegramAPIHash,
                                    }));
                                }}
                                telegramPhoneNumber={settings.telegramPhoneNumber}
                                onTelegramPhoneNumberChange={(telegramPhoneNumber) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        telegramPhoneNumber,
                                    }));
                                }}
                            />
                        }
                    />
                    <Route
                        path='ErcTokenCreate'
                        element={
                            <ErcTokenCreate
                                ethPrivateKey={settings.ethPrivateKey}
                                ethSniperPrivateKeys={settings.ethSniperPrivateKeys}
                                ethSniperPercentages={settings.ethSnipeValues}
                                ethInitialLiquidityCreation={settings.ethLiquidityCopy}
                                solidityReleases={solidityReleases}
                                provider={ethProvider}
                                telegramAPIID={settings.telegramAPIID}
                                telegramAPIHash={settings.telegramAPIHash}
                                telegramPhoneNumber={settings.telegramPhoneNumber}
                            />
                        }
                    />
                </Routes>
                <ToastContainer position='bottom-right' theme='dark' />
            </div>
            <Modal header='Customize Feed' open={feedDisplayOpen} onClose={() => setFeedDisplayOpen(false)}>
                {!locationWithoutHeader && (
                    <div className='flex flex-wrap min-w-0 max-w-full gap-[10px]'>
                        <div className='dyn-inputs'>
                            <label className={`custom-checkbox ${isGeneralPostsVisible ? 'checked' : ''}`}>
                                <span className='label-text'>Everyone</span>
                                <input type='checkbox' checked={isGeneralPostsVisible} onChange={(e) => visibleGeneralPosts(e.target.checked)} />
                                <span className='custom-checkbox-box'>
                                    <span className='checkmark'>✔</span>
                                    <span className='cross'>✖</span>
                                </span>
                            </label>
                        </div>
                        <div className='dyn-inputs '>
                            <label className={`custom-checkbox pumpfuns-header-main ${isBonkPostsVisible ? 'checked' : ''}`}>
                                <span className='label-text'>LetsBonk</span>
                                <input type='checkbox' checked={isBonkPostsVisible} onChange={(e) => visibleBonkPosts(e.target.checked)} />
                                <span className='custom-checkbox-box pumpfuns-custom-checkbox-box'>
                                    <span className='checkmark'>✔</span>
                                    <span className='cross'>✖</span>
                                </span>
                            </label>
                        </div>
                        <div className='dyn-inputs '>
                            <label className={`custom-checkbox pumpfuns-header-main ${isPumpFunsPostsVisible ? 'checked' : ''}`}>
                                <span className='label-text'>PumpFuns</span>
                                <input type='checkbox' checked={isPumpFunsPostsVisible} onChange={(e) => visiblePumpFunsPosts(e.target.checked)} />
                                <span className='custom-checkbox-box pumpfuns-custom-checkbox-box'>
                                    <span className='checkmark'>✔</span>
                                    <span className='cross'>✖</span>
                                </span>
                            </label>
                        </div>
                        {sourceOrder.map((username, index) => {
                            const source = sources.find((s) => s.username === username);
                            return (
                                source && (
                                    <Header
                                        key={index}
                                        source={source}
                                        onToggle={handleToggleSource}
                                        isChecked={visibleSources.some((s) => s.username === source.username)}
                                        sourceOrder={sourceOrder}
                                        handleReorder={handleReorder}
                                    />
                                )
                            );
                        })}
                    </div>
                )}
            </Modal>
        </DndProvider>
    );
}

function UnauthorizedApp() {
    const { login, register } = useBackend();
    const [accessCode, setAccessCode] = useState('');
    const [error, setError] = useState('');

    const handleRegister = () => {
        if (!accessCode.trim()) {
            setError('Access code is required for registration.');
            return;
        }
        setError('');
        register(accessCode).then(() => {
            alert('Registered');
        });
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
            }}
        >
            <div
                style={{
                    background: 'rgba(30, 32, 38, 0.95)',
                    borderRadius: '18px',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                    padding: '40px 32px',
                    width: '100%',
                    maxWidth: 380,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    border: '1px solid rgba(255,255,255,0.08)',
                }}
            >
                <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 18, color: '#fff', letterSpacing: 1 }}>Authentication Required</h1>
                <button
                    style={{
                        width: '100%',
                        padding: '12px 0',
                        marginBottom: 22,
                        background: 'linear-gradient(90deg, #00c6ff 0%, #0072ff 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 16,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    }}
                    onClick={() =>
                        login().then(() => {
                            alert('Logged in');
                        })
                    }
                >
                    Login with Google
                </button>
                <input
                    style={{
                        marginBottom: 12,
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '1px solid #444',
                        width: '100%',
                        fontSize: 15,
                        background: '#232526',
                        color: '#fff',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border 0.2s',
                    }}
                    type='text'
                    placeholder='Access code for registration'
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                />
                <button
                    style={{
                        width: '100%',
                        padding: '12px 0',
                        background: 'linear-gradient(90deg, #ff512f 0%, #dd2476 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 16,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    }}
                    onClick={handleRegister}
                >
                    Register with Access Code
                </button>
                {error && <span style={{ color: '#ff6b6b', marginTop: 14, fontWeight: 500 }}>{error}</span>}
            </div>
        </div>
    );
}

function App() {
    const { me } = useBackend();

    const [user, setUser] = useState<Api.Auth.User | null>(null);

    useEffect(() => {
        const fetchUser = () =>
            me()
                .then((theUser) => {
                    setUser((currentUser) => (currentUser ? currentUser : theUser));
                })
                .catch((reason) => {
                    console.log(reason);
                    setUser((prev) => (typeof reason == 'object' && 'status' in reason && reason.status === 401 ? null : prev));
                });

        const interval = setInterval(fetchUser, 60000);
        fetchUser();
        return () => clearInterval(interval);
    }, [me]);

    return user ? <AuthorizedApp user={user} /> : <UnauthorizedApp />;
}

export default App;
